import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  AiOutlineZoomIn,
  AiOutlineZoomOut,
  AiOutlineFullscreen,
  AiOutlineReload,
} from "react-icons/ai";
import styles from "../../../styles/Components/bot/bot-reports/PDFViewer.module.css";
import SharePdf from "./SharePdf";
import { FaRegFilePdf } from "react-icons/fa";
import { message } from "antd";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function PDFViewer({ url }) {
  const [numPages, setNumPages] = useState(null);
  const [pageOriginalWidth, setPageOriginalWidth] = useState(null);
  const [fitScale, setFitScale] = useState(1);
  const [scale, setScale] = useState(1);
  const [isFitted, setIsFitted] = useState(true);
  const containerRef = useRef(null);

  const ZOOM_STEP = 0.1;
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 3;

  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const onPageLoadSuccess = (page) => {
    try {
      const viewport = page.getViewport({ scale: 1 });
      if (!pageOriginalWidth) setPageOriginalWidth(viewport.width);
    } catch (e) {
      console.warn("Viewport error:", e);
    }
  };

  useEffect(() => {
    if (!containerRef.current || !pageOriginalWidth) return;

    const computeFitScale = () => {
      const paddingX = 32;
      const containerWidth = Math.max(
        200,
        containerRef.current.clientWidth - paddingX,
      );
      const newFit = Math.max(0.1, containerWidth / pageOriginalWidth);
      setFitScale(newFit);
      setScale((prev) => (isFitted ? newFit : prev));
    };

    computeFitScale();

    const ro = new ResizeObserver(() => computeFitScale());
    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [pageOriginalWidth, isFitted]);

  const zoomIn = () => {
    setIsFitted(false);
    setScale((s) => Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2)));
  };

  const zoomOut = () => {
    setIsFitted(false);
    setScale((s) => Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2)));
  };

  const fitToPage = () => {
    setIsFitted(true);
    setScale(fitScale);
  };

  const resetZoom = () => {
    setIsFitted(true);
    setScale(fitScale);
  };

  const exportPDF = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "performance-report.pdf";
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      // alert("Failed to download the PDF file.");
      message.error("Failed to download the PDF file.");
    }
  };

  // const sharePDF = async () => {
  //   if (navigator.share) {
  //     try {
  //       await navigator.share({
  //         title: "PDF Report",
  //         text: "Check out this PDF report!",
  //         url: url,
  //       });
  //     } catch (err) {
  //       console.error("Error sharing:", err);
  //     }
  //   } else {
  //     navigator.clipboard.writeText(url);
  //     alert("Link copied to clipboard!");
  //   }
  // };

  return (
    <div className={styles.container}>
      <div className={styles.controllers}>
        <span
          className={styles.controllerBtn}
          onClick={zoomOut}
          title="Zoom Out"
          aria-label="Zoom out"
        >
          <AiOutlineZoomOut size={18} />
        </span>

        <span
          className={styles.controllerBtn}
          onClick={zoomIn}
          title="Zoom In"
          aria-label="Zoom in"
        >
          <AiOutlineZoomIn size={18} />
        </span>

        <span
          className={styles.controllerBtn}
          onClick={fitToPage}
          title="Fit to Page"
          aria-label="Fit to page"
        >
          <AiOutlineFullscreen size={18} />
        </span>

        <span
          className={styles.controllerBtn}
          onClick={resetZoom}
          title="Reset to 100%"
          aria-label="Reset zoom"
        >
          <AiOutlineReload size={18} />
        </span>

        {/*<span*/}
        {/*  className={styles.controllerBtn}*/}
        {/*  onClick={sharePDF}*/}
        {/*  title="Share PDF"*/}
        {/*  aria-label="Share PDF"*/}
        {/*>*/}
        {/*  <AiOutlineShareAlt size={18} />*/}
        {/*</span>*/}
        <SharePdf link={url} />

        <span
          className={styles.controllerBtn}
          onClick={exportPDF}
          title="Download PDF"
          aria-label="Download PDF"
        >
          <FaRegFilePdf size={18} />
        </span>

        {/*<div style={{ fontSize: 14, color: "#333" }}>*/}
        {/*  {Math.round(scale * 100)}%*/}
        {/*  {isFitted && (*/}
        {/*    <span style={{ color: "#888", marginLeft: 4 }}>(fit)</span>*/}
        {/*  )}*/}
        {/*</div>*/}
      </div>

      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "16px",
        }}
      >
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(e) => console.error("PDF load error:", e)}
          loading={<div>Loading PDF...</div>}
        >
          {Array.from(new Array(numPages), (el, index) => (
            <div
              key={`page_${index + 1}`}
              style={{
                marginBottom: 16,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 8,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Page
                pageNumber={index + 1}
                scale={scale}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={true}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
}

export default PDFViewer;
